import { useCallback, useMemo } from 'react';

import useDailyChosenState from './useDailyChosenState';
import { BookingSlot } from '../../api/search';
import { Kid } from '../../components/KidsInput';
import { ChosenSlots, DateSlots, DayTime } from '../../contexts/checkout';
import { BookingParams } from '../../native/types';
import { getDateAsDatestring } from '../../utils/date';
import { transformSlots } from '../../utils/slots';

export default function useDailyLogic(
  slots: BookingSlot[],
  kidsWithEligibility: Kid[],
  isLoggedIn: boolean,
  initialState: ChosenSlots
) {
  const dateSlots = useMemo(
    () => transformSlots(slots, kidsWithEligibility, isLoggedIn),
    [slots, kidsWithEligibility, isLoggedIn]
  );
  const availableDates = Object.keys(dateSlots);

  const isDayAvailable = useIsDayAvailable(availableDates);

  const {
    chosenSlots,
    chosenSlotsEntries,
    onDateSelect,
    removeSlot,
    overwriteChosenSlots,
  } = useDailyChosenState(initialState, dateSlots, slots);

  const getCurrentChosenSlotsForDate = (datestring: string) =>
    chosenSlots[datestring] || [];

  const finalSelectionData = useMemo(() => {
    const availableSlots = Object.keys(chosenSlots).reduce(
      (acc, dateString) => {
        const dayTimes = chosenSlots[dateString]!.filter((dayTime: DayTime) => {
          const availableSlot = dateSlots[dateString]?.[dayTime];
          return !!availableSlot;
        });
        if (dayTimes.length > 0) {
          acc[dateString] = dayTimes;
        }
        return acc;
      },
      {} as ChosenSlots
    );

    return {
      dateSlots,
      chosenSlots: availableSlots,
      kids: kidsWithEligibility,
      bookingMode: 'date_range' as const,
    };
  }, [chosenSlots, dateSlots, kidsWithEligibility]);

  const overwriteChosenSlotsFn = useCallback(
    (chosenSlots: ChosenSlots) => {
      overwriteChosenSlots(chosenSlots);
      return getFinalSelectionData(chosenSlots, dateSlots, kidsWithEligibility);
    },
    [slots, dateSlots, kidsWithEligibility]
  );

  return {
    selection: {
      chosenSlots,
      chosenSlotsEntries,
    },
    availability: {
      dateSlots,
    },
    api: {
      isDayAvailable,
      getCurrentChosenSlotsForDate,

      onDateSelect,
      removeSlot,
      overwriteChosenSlots: overwriteChosenSlotsFn,

      finalSelectionData,
    },
  };
}

const getFinalSelectionData = (
  chosenSlots: ChosenSlots,
  dateSlots: DateSlots,
  kidsWithEligibility: Kid[]
): BookingParams => {
  const availableSlots = Object.keys(chosenSlots).reduce((acc, dateString) => {
    const dayTimes = chosenSlots[dateString]!.filter((dayTime: DayTime) => {
      const availableSlot = dateSlots[dateString]?.[dayTime];
      return !!availableSlot;
    });
    if (dayTimes.length > 0) {
      acc[dateString] = dayTimes;
    }
    return acc;
  }, {} as ChosenSlots);
  return {
    dateSlots,
    chosenSlots: availableSlots,
    kids: kidsWithEligibility,
    bookingMode: 'date_range' as const,
  };
};

const useIsDayAvailable = (availableDates: string[]) =>
  useCallback(
    (datestring: string) => {
      const todayDatestring = getDateAsDatestring();

      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + 365);
      const maxDateString = getDateAsDatestring(maxDate);

      return (
        datestring >= todayDatestring &&
        datestring < maxDateString &&
        availableDates.includes(datestring)
      );
    },
    [availableDates]
  );
